import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { MaterialReactTable } from 'material-react-table';
import { useAppSelector } from '../../app/hooks'
import './index.css';
import { League, Player, RosterPositionEnum, LeaguePhase } from '../../sdk/model';
import { getColorByPosition } from '../util/ColorByPosition';
import { dataloadToPlayerDataFlat, isPlayerLocked } from '../util/PlayerDataUtil';
import { useLoadLeagueData } from '../../app/dataLoaderHooks';
import {Box, FormControl, Modal, RadioGroup, Radio, FormControlLabel, InputLabel, Checkbox, Button, Select, MenuItem, useTheme, useMediaQuery, Card, CardContent, Chip, Stack, Typography, Avatar} from '@mui/material';
import PlayerInfoCard from '../cards/playerCard';
import { getUsersTeamFromLeague } from '../util/TeamUtils';
import BidOnPlayerButton from '../body/playerClaims/BidOnPlayerButton';
import { getTotalScoreFromStats } from '../util/ScoreService';
import { playerClaimApi } from '../../adapters/APIExporter';
import CircularProgress from '@mui/material/CircularProgress';
import playerMatchupDisplay from '../util/PlayerMatchupDisplay';

//Using https://www.material-react-table.com/docs/examples/basic

const ACTIVE_LEAGUE_PHASES = new Set([LeaguePhase.RegularSeason, LeaguePhase.Playoffs]);

// Compact Player Card Component for Search Table
const CompactPlayerCard = React.memo(({ 
  player, 
  fantasyTeam, 
  projection, 
  league, 
  nflGameMap, 
  onPlayerClick 
}: {
  player: Player;
  fantasyTeam: string;
  projection: number;
  league: League;
  nflGameMap: Map<string, any>;
  onPlayerClick: (player: Player) => void;
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const currentWeekStats = player?.quick_stats?.currentWeeksStats;
  const nflGame = currentWeekStats ? nflGameMap.get(player.nfl_team?.alias!) : undefined;
  const currentWeek = league.currentSeason?.currentWeek === league.currentSeason?.currentWeek;
  
  // Get matchup info
  const matchupInfo = React.useMemo(() => {
    if (!nflGame && !currentWeekStats) return "BYE";
    
    if (!nflGame) {
      // Handle case where opponent is undefined
      if (!currentWeekStats?.opponent) return "BYE";
      const opponentString = (currentWeekStats?.isHome ? "" : "@") + currentWeekStats?.opponent;
      if (currentWeekStats?.startDateTime) {
        const gametime = new Date(currentWeekStats.startDateTime);
        return `${opponentString} ${gametime.toLocaleDateString(undefined, { weekday: 'short' })} ${gametime.toLocaleTimeString([], {hour: 'numeric', minute: '2-digit'})}`;
      }
      return opponentString;
    }
    
    const opponentString = nflGame.awayTeamAlias === player.nfl_team?.alias ? 
      "@" + nflGame.homeTeamAlias : nflGame.awayTeamAlias;
    
    if (nflGame.quarter || ['COMPLETE', 'CLOSED'].includes(nflGame.status!)) {
      const gameTime = nflGame.status === 'COMPLETE' || nflGame.status === 'CLOSED' ? 'Final' : 
                      nflGame.clock === '00:00' && nflGame.quarter === 2 ? 'HALF' :
                      nflGame.clock === '00:00' && nflGame.quarter === 4 ? 'End' :
                      nflGame.quarter! > 4 ? 'OT' : `Q${nflGame.quarter}`;
      return `${opponentString} ${gameTime} ${nflGame.awayPts}-${nflGame.homePts}`;
    }
    
    if (currentWeekStats?.startDateTime) {
      const gametime = new Date(currentWeekStats.startDateTime);
      return `${opponentString} ${gametime.toLocaleDateString(undefined, { weekday: 'short' })} ${gametime.toLocaleTimeString([], {hour: 'numeric', minute: '2-digit'})}`;
    }
    
    return opponentString;
  }, [player, currentWeekStats, nflGame]);
  
  const isLive = !!nflGame?.quarter && !['COMPLETE', 'CLOSED'].includes(nflGame.status!);
  
  return (
    <Box 
      onClick={() => onPlayerClick(player)}
      sx={{
        p: { xs: 1, sm: 1.5 },
        cursor: 'pointer',
        borderRadius: 1,
        backgroundColor: isLive ? 'rgba(76, 175, 80, 0.1)' : 'transparent',
        border: isLive ? '1px solid rgba(76, 175, 80, 0.3)' : 'none',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.04)'
        },
        minHeight: { xs: '80px', sm: '90px' },
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center'
      }}
    >
      {/* Player Name and Avatar */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
        <Avatar 
          alt={player?.abbr_name} 
          src={player?.picture}
          sx={{ 
            width: { xs: 24, sm: 32 }, 
            height: { xs: 24, sm: 32 }, 
            mr: 1
          }}
        />
        <Typography 
          variant="body2"
          sx={{ 
            fontSize: { xs: '0.85rem', sm: '0.95rem' }, 
            fontWeight: 'bold',
            color: 'black',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1
          }}
        >
          {player.abbr_name || `${player.first_name} ${player.last_name}`}
        </Typography>
      </Box>
      
      {/* Position and Team */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography 
          variant="caption"
          sx={{ 
            fontSize: { xs: '0.7rem', sm: '0.75rem' }, 
            color: 'black',
            fontWeight: 'medium'
          }}
        >
          {player.position} - {player.nfl_team?.alias}
        </Typography>
        {fantasyTeam && (
          <Chip
            label={fantasyTeam}
            size="small"
            sx={{
              height: { xs: 14, sm: 16 },
              fontSize: { xs: '0.55rem', sm: '0.65rem' },
              backgroundColor: 'primary.main',
              color: 'primary.contrastText'
            }}
          />
        )}
      </Box>
      
      {/* Matchup Info */}
      <Typography
        variant="caption"
        sx={{ 
          fontSize: { xs: '0.65rem', sm: '0.7rem' }, 
          color: 'black',
          mb: 0.5,
          lineHeight: 1.2
        }}
      >
        {matchupInfo}
      </Typography>
      
      {/* Projection */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography
          variant="caption"
          sx={{
            fontSize: { xs: '0.65rem', sm: '0.7rem' },
            color: 'black'
          }}
        >
          Proj: <strong>{projection.toFixed(1)}</strong>
        </Typography>
        {isLive && (
          <Chip
            label="LIVE"
            size="small"
            sx={{
              height: { xs: 12, sm: 14 },
              fontSize: { xs: '0.5rem', sm: '0.55rem' },
              backgroundColor: 'success.main',
              color: 'success.contrastText'
            }}
          />
        )}
      </Box>
    </Box>
  );
});

const PlayerSearch = ({}) => {
  const {leagueId} = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [isLoadComplete, setLoadComplete] = React.useState(false);
  const [isException, setIsException] = React.useState(false);
  const [viewingPlayer, setViewingPlayer] = React.useState<Player | undefined>(undefined);
  const [includeExpired, setIncludeExpired] = React.useState(true);
  const [isPoachingAllowed, setIsPoachingAllowed] = React.useState(false);
  const [positionFilter, setPositionFilter] = React.useState("ALL");
  const [downloading, setDownloading] = React.useState<any>(undefined);
  
  const user = useAppSelector((state) => state.user);

  const league : League|undefined = useLoadLeagueData({leagueId: leagueId, userId: user.id, loadBasicDraftData: false,
                                                              loadFullDraftData: false, loadContractData: false,
                                                              loadBasicAuctionData: false, loadFullAuctionData: false,
                                                              loadRosterData: true, loadWaivedContracts: true, loadRosterFor: [], loadScheduleData: false,
                                                              isException: isException, setIsException: setIsException,
                                                              isLoadComplete: isLoadComplete, setLoadComplete: setLoadComplete});
  
  React.useEffect(() =>{
    playerClaimApi.isPoachingAllowed().then(response => {
      setIsPoachingAllowed(response.data)
    })
  }, [])
  
  React.useEffect(() => {
    if (downloading) {
      setTimeout(function (){
        const headers = "Player Name,Position,NFL Team,Fantasy Team,Rank,Projection\n";
        const rowData = downloading.map((row) => {
          const player = row.original;
          return [
            player.abbr_name || `${player.first_name} ${player.last_name}`,
            player.position || '',
            player.nfl_team?.alias || '',
            player.fantasyTeam || '',
            player.quick_stats?.rankOnYear === 999 ? '' : player.quick_stats?.rankOnYear || '',
            player.projection?.toFixed(1) || ''
          ].join(',')
        }).join('\n');
        const csvData = headers + rowData;
        let blob = new Blob([csvData], { type: 'text/csv' });
        let url = window.URL.createObjectURL(blob);
        let a = document.createElement('a');
        a.href = url;
        a.download = league?.leagueName + ' - Players.csv';
        document.body.appendChild(a);
        a.click();
        setDownloading(undefined);     
      }, 1);
      
    }
  }, [downloading, league?.leagueName])
  
  // TODO: Make this happen at the top level so we are not reloading the data every time
  const playerDataLoad = useAppSelector((state) => state.playerDataLoad );
  const nflGames = useAppSelector((state) => state.nflGameDataLoad );
  const myTeam = getUsersTeamFromLeague(league, user?.id);
  
  // Create NFL game map
  const nflGameMap = React.useMemo(() => 
    new Map(nflGames.flatMap(nflGame => {
      const game = nflGame as any; // Type assertion since model is missing team alias properties
      return [[game.awayTeamAlias, nflGame], [game.homeTeamAlias, nflGame]];
    })), 
    [nflGames]
  );
  
  // Mobile columns with compact card
  const mobileColumns = React.useMemo(() => [
      {
        accessorFn: (row) => `${row.first_name || ''} ${row.last_name || ''} ${row.abbr_name || ''}`.trim(),
        id: 'playerInfo',
        header: 'Player Information',
        minSize: 200,
        size: 200,
        enableSorting: false,
        enableColumnFilter: false,
        enableGlobalFilter: true,
        Cell: ({ row }) => (
          <CompactPlayerCard
            player={row.original}
            fantasyTeam={row.original.fantasyTeam || ''}
            projection={row.original.projection || 0}
            league={league!}
            nflGameMap={nflGameMap}
            onPlayerClick={setViewingPlayer}
          />
        )
      },
      {
        accessorKey: 'quick_stats.rankOnYear',
        id: 'rank',
        header: 'Rank',
        minSize: isSmallMobile ? 50 : 60,
        size: isSmallMobile ? 50 : 60,
        Cell: ({ cell }) => (
          <Box sx={{ textAlign: 'center', py: 1 }}>
            <Typography 
              variant="body2" 
              sx={{ 
                fontSize: { xs: '0.8rem', sm: '0.9rem' },
                fontWeight: 'bold',
                color: 'black'
              }}
            >
              {cell.getValue() === 999 ? '-' : cell.getValue()}
            </Typography>
          </Box>
        )
      },
      {
        accessorKey: 'projection',
        id: 'projection',
        header: 'Proj',
        minSize: 0,
        size: 0,
        enableHiding: true,
        // This column is hidden but needed for sorting
      }
    ], [isSmallMobile, league, nflGameMap, setViewingPlayer]);
  
  // Desktop columns with traditional table layout
  const desktopColumns = React.useMemo(() => [
      {
        accessorFn: (row) => `${row.first_name || ''} ${row.last_name || ''} ${row.abbr_name || ''}`.trim(),
        id: 'name',
        header: 'Player',
        minSize: 180,
        size: 180,
        enableGlobalFilter: true,
        Cell: ({ row }) => (
          <Box 
            onClick={() => setViewingPlayer(row.original)}
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              cursor: 'pointer',
              '&:hover': { opacity: 0.8 }
            }}
          >
            <Avatar 
              alt={row.original.abbr_name} 
              src={row.original.picture}
              sx={{ width: 40, height: 40, mr: 1.5 }}
            />
            <Typography 
              variant="body2" 
              sx={{ fontWeight: 'bold', color: 'black' }}
            >
              {row.original.abbr_name || `${row.original.first_name} ${row.original.last_name}`}
            </Typography>
          </Box>
        )
      },
      {
        accessorKey: 'nfl_team.alias',
        id: 'team',
        header: 'Team',
        minSize: 80,
        size: 80,
        enableGlobalFilter: true,
        Cell: ({ cell }) => (
          <Typography variant="body2" sx={{ color: 'black' }}>
            {cell.getValue()}
          </Typography>
        )
      },
      {
        accessorKey: 'fantasyTeam',
        id: 'fantasyTeam',
        header: 'Fantasy Team',
        minSize: 140,
        size: 140,
        Cell: ({ cell }) => (
          cell.getValue() ? (
            <Chip
              label={cell.getValue()}
              size="small"
              sx={{
                backgroundColor: 'primary.main',
                color: 'primary.contrastText'
              }}
            />
          ) : null
        )
      },
      {
        accessorKey: 'quick_stats.rankOnYear',
        id: 'rank',
        header: 'Rank',
        minSize: 80,
        size: 80,
        Cell: ({ cell }) => (
          <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'black' }}>
            {cell.getValue() === 999 ? '-' : cell.getValue()}
          </Typography>
        )
      },
      {
        accessorKey: 'projection',
        id: 'projection',
        header: 'Projection',
        minSize: 100,
        size: 100,
        Cell: ({ cell }) => (
          <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'black' }}>
            {cell.getValue()?.toFixed(1) || '0.0'}
          </Typography>
        )
      }
    ], [setViewingPlayer]);
  
  const columns = React.useMemo(() => 
    isMobile ? mobileColumns : desktopColumns,
    [isMobile, mobileColumns, desktopColumns]
  );
  
  console.log("Player Data Load: ");
  console.log(playerDataLoad);
  
  // IMPORTANT: This is where we are loading the data into the table
  const [data, setData] = React.useState<Player[]>([])
  const [availability, setAvailability] = React.useState("available");
  
  const playerMap = new Map(playerDataLoad.flatMap(team => team.players || []).map((player) => [player.id, player]));

  React.useEffect(() => {
    setData(dataloadToPlayerDataFlat(playerDataLoad));
  }, [playerDataLoad]);
  
  if (!isLoadComplete || !league?.waivedContractsLoaded) {
    return <p>Loading...</p>
  }
  
  // Check if all team rosters are loaded
  const allRostersLoaded = league?.teams?.every(team => team.rosterLoaded) || false;
  if (!allRostersLoaded) {
    return <p>Loading rosters...</p>
  }
  
  const playerIdToTeamMap = new Map(league?.teams.flatMap(team =>  {
    const playerRoster = team.playerRoster ? team.playerRoster : [];
    return Object.values(playerRoster).flat().filter(player => player).map(player => [player.playerId, team]);
  }));

  console.log("setPlayerSelection is not null, rendering row selection")
  const practiceSquadPlayerTeamMap = new Map(league?.teams.flatMap(team => {
    const practiceSquad = team.practiceSquad ? team.practiceSquad : [];
    return practiceSquad.filter(player => player).map(player => [player.playerId, team]);
  }))
  const practiceSquadPlayerIds = new Set(practiceSquadPlayerTeamMap.keys());
  // Mark Player as unranked if we did not set one?
  let filteredData = data.map((player) => {
    const fantasyTeam = playerIdToTeamMap.get(player.id) || practiceSquadPlayerTeamMap.get(player.id)
    const fantasyTeamName = fantasyTeam?.name ? fantasyTeam.name + (practiceSquadPlayerIds.has(player.id) ? " (PS)" : "") : "";
    
    // For availability filter: only show fantasy team name if the player is actually owned
    const displayFantasyTeam = availability === 'available' ? "" : fantasyTeamName;
    
    if (player?.quick_stats?.rankOnYear === -1) {
      return {
        ...player,
        quick_stats: {
          ...player.quick_stats,
          rankOnYear: 999,
        },
        fantasyTeam: displayFantasyTeam,
        projection: getTotalScoreFromStats(player.quick_stats.currentWeeksStats?.projections, league.currentSeason?.scoringSettings!, player.position ?? "ALL", undefined) || 0
      };
    }
    return {
      ...player,
      fantasyTeam: displayFantasyTeam,
      projection: getTotalScoreFromStats(player.quick_stats?.currentWeeksStats?.projections, league.currentSeason?.scoringSettings!, player.position ?? "ALL", undefined) || 0
    };
  });

  const unavailablePlayers = new Set(league?.teams.flatMap(team => {
    const playerRoster = team.playerRoster ? team.playerRoster : [];
    return Object.values(playerRoster).flat().filter(player => player && (!includeExpired || !player.contractExpired)).map(player => player!.playerId);
  }));
  const waivedPlayerTeamMap = new Map(league?.waivedContracts!.map(contract => [contract.playerId, contract.teamId]));
  const waivedPlayerIds = new Set(waivedPlayerTeamMap.keys());
  if (availability === 'available') {
    filteredData = filteredData.filter((player) => !unavailablePlayers.has(player?.id));
  } else if (availability === 'waived') {
    filteredData = filteredData.filter((player) => waivedPlayerIds.has(player?.id));
  } else if (availability === 'practiceSquad') {
    filteredData = filteredData.filter((player) => practiceSquadPlayerIds.has(player?.id));
  }
  if (positionFilter === 'QB') {
    filteredData = filteredData.filter(player => player.position === 'QB')
  } else if (positionFilter === 'RB') {
    filteredData = filteredData.filter(player => player.position === 'RB')
  } else if (positionFilter === 'WR') {
    filteredData = filteredData.filter(player => player.position === 'WR')
  } else if (positionFilter === 'TE') {
    filteredData = filteredData.filter(player => player.position === 'TE')
  } else if (positionFilter === 'FLEX') {
    filteredData = filteredData.filter(player => ['RB', 'WR', 'TE'].includes(player.position!))
  }
  return <Box sx={{ p: { xs: 1, sm: 2 } }}>
    <Modal open={viewingPlayer !== undefined} onClose={() => setViewingPlayer(undefined)} 
        aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description"
        sx={{margin: isMobile ? '2%' : '5%'}}>
      <PlayerInfoCard player={viewingPlayer!} leagueId={leagueId}/>
    </Modal>
    
    {/* Mobile-optimized filter controls */}
    <Card sx={{ mb: 2, p: { xs: 1, sm: 2 } }}>
      <CardContent sx={{ p: { xs: 1, sm: 2 }, '&:last-child': { pb: { xs: 1, sm: 2 } } }}>
        <Stack spacing={2}>
          {/* Player availability filter */}
          <Box>
            <FormControl fullWidth>
              <RadioGroup
                name="radio-buttons-group"
                value={availability}
                onChange={(event) => setAvailability(event.target.value)}
                sx={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  flexWrap: 'wrap',
                  gap: { xs: 0.5, sm: 1 }
                }}>
                <FormControlLabel 
                  value="available" 
                  control={<Radio size={isMobile ? 'small' : 'medium'} />} 
                  label="Available"
                  sx={{ 
                    '& .MuiFormControlLabel-label': { 
                      fontSize: { xs: '0.875rem', sm: '1rem' }
                    }
                  }}
                />
                <FormControlLabel 
                  value="waived" 
                  control={<Radio size={isMobile ? 'small' : 'medium'} />} 
                  label="Waived"
                  sx={{ 
                    '& .MuiFormControlLabel-label': { 
                      fontSize: { xs: '0.875rem', sm: '1rem' }
                    }
                  }}
                />
                {!!league.rosterSettings![RosterPositionEnum.Ps] && 
                  <FormControlLabel 
                    value="practiceSquad" 
                    control={<Radio size={isMobile ? 'small' : 'medium'} />} 
                    label="Practice Squad"
                    sx={{ 
                      '& .MuiFormControlLabel-label': { 
                        fontSize: { xs: '0.875rem', sm: '1rem' }
                      }
                    }}
                  />
                }
                <FormControlLabel 
                  value="all" 
                  control={<Radio size={isMobile ? 'small' : 'medium'} />} 
                  label="All"
                  sx={{ 
                    '& .MuiFormControlLabel-label': { 
                      fontSize: { xs: '0.875rem', sm: '1rem' }
                    }
                  }}
                />
              </RadioGroup>
            </FormControl>
          </Box>
          
          {/* Position filter dropdown */}
          <Box>
            <FormControl fullWidth size={isMobile ? 'small' : 'medium'}>
              <InputLabel id="position-filter-label">Position</InputLabel>
              <Select 
                labelId="position-filter-label"
                name="positionFilter" 
                value={positionFilter}
                label="Position"
                onChange={(event) => setPositionFilter(event.target.value)}
                sx={{ 
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }}
              >
                <MenuItem value='ALL'>All Positions</MenuItem>
                <MenuItem value='QB'>QB</MenuItem>
                <MenuItem value='RB'>RB</MenuItem>
                <MenuItem value='WR'>WR</MenuItem>
                <MenuItem value='TE'>TE</MenuItem>
                <MenuItem value='FLEX'>FLEX (RB/WR/TE)</MenuItem>
              </Select>
            </FormControl>
          </Box>
          
          {/* Additional controls */}
          {availability === 'available' && league.phase === LeaguePhase.Offseason && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Checkbox 
                checked={includeExpired} 
                onChange={() => setIncludeExpired(!includeExpired)}
                size={isMobile ? 'small' : 'medium'}
              />
              <InputLabel 
                id="expiredLabel"
                sx={{ 
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  cursor: 'pointer'
                }}
                onClick={() => setIncludeExpired(!includeExpired)}
              >
                Include players on Expired Contracts?
              </InputLabel>
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>

    <MaterialReactTable 
      enableMultiRowSelection={false}
      getRowId={(row) => row.id || ''}
      columns={columns} 
      data={filteredData} 
      enableRowActions
      enableGlobalFilter={true}
      enableDensityToggle={!isMobile}
      enableFullScreenToggle={!isMobile}
      enableColumnFilters={!isSmallMobile}
      enableGlobalFilterModes={!isSmallMobile}
      enableColumnActions={!isSmallMobile}
      
      // Mobile-specific settings
      muiTableProps={{
        sx: {
          '& .MuiTableCell-root': {
            padding: { xs: '8px 4px', sm: '16px' },
            fontSize: { xs: '0.75rem', sm: '0.875rem' }
          }
        }
      }}
      
      renderTopToolbarCustomActions={({table}) => 
        downloading ? <CircularProgress size={isMobile ? 20 : 24} /> :
        <Button 
          className="capsized-button" 
          onClick={() => setDownloading(table.getPrePaginationRowModel().rows)}
          size={isMobile ? "small" : "medium"}
          sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
        >
          {isMobile ? 'CSV' : 'Download to CSV'}
        </Button>
      }
      
      initialState={league.phase === LeaguePhase.RegularSeason || league.phase === LeaguePhase.Playoffs ? {
        sorting: [
            {
              id: 'projection',
              desc: true,
            },
          ],
        columnVisibility: {
          rank: false,
          ...(isMobile ? { projection: false } : {}),
        },
        pagination: { 
          pageSize: isMobile ? 10 : 25,
          pageIndex: 0 
        }
        } : {
        sorting: [
          {
            id: 'rank',
            desc: false,
          },
        ],
        columnVisibility: {
          ...(isMobile ? { projection: false } : {}),
        },
        pagination: { 
          pageSize: isMobile ? 10 : 25,
          pageIndex: 0 
        }
      }}
      
      renderRowActions={({ row }) => {
        const owningTeam = playerIdToTeamMap.get(row.id);
        if (owningTeam) {
          if (owningTeam.id === myTeam?.id) {
            return null;
          }
          const player = Object.values(owningTeam.playerRoster!).flat().find(player => player && player.playerId === row.id);
          if (player?.contractExpired) {
            return null;
          }
          return <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%', px: 1 }}>
            <Button 
              color="primary" 
              variant="contained" 
              size={isMobile ? "small" : "large"}
              fullWidth
              sx={{
                fontSize: { xs: '0.7rem', sm: '0.95rem' },
                padding: { xs: '4px 8px', sm: '12px 24px' },
                minHeight: { xs: '32px', sm: '48px' }
              }} 
              onClick={() => navigate('/league/' + leagueId + '/trade?teamId=' + owningTeam.id + '&playerId=' + row.id)}
            >
              {isMobile ? 'Trade' : 'Propose Trade'}
            </Button>
          </Box>
        }
        const waivedTeamId = waivedPlayerTeamMap.get(row.id);
        if (waivedTeamId) {
          return <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%', px: 1 }}>
            <Button 
              color="primary" 
              variant="contained" 
              disabled={waivedTeamId === myTeam?.id} 
              size={isMobile ? "small" : "large"}
              fullWidth
              sx={{
                fontSize: { xs: '0.7rem', sm: '0.95rem' },
                padding: { xs: '4px 8px', sm: '12px 24px' },
                minHeight: { xs: '32px', sm: '48px' }
              }} 
              onClick={() => navigate('/league/' + leagueId + '/team/' + myTeam?.id + '/waivers/' + row.id)}
            >
              Claim
            </Button>
          </Box>
        }
        const practiceSquadTeam = practiceSquadPlayerTeamMap.get(row.id);
        if (practiceSquadTeam?.id) {
          const player = playerMap.get(row.id);
          const locked = (league.phase === LeaguePhase.RegularSeason || league.phase === LeaguePhase.Playoffs) && (!isPoachingAllowed || isPlayerLocked(player));
          return <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%', px: 1 }}>
            <div title={locked ? "Players are locked from poaching from their kickoff time until 24 hours after the start of the following week" : ""} style={{ width: '100%' }}>
              <Button 
                color="primary" 
                variant="contained" 
                disabled={practiceSquadTeam.id === myTeam?.id || !!locked} 
                size={isMobile ? "small" : "large"}
                fullWidth
                sx={{
                  fontSize: { xs: '0.7rem', sm: '0.95rem' },
                  padding: { xs: '4px 8px', sm: '12px 24px' },
                  minHeight: { xs: '32px', sm: '48px' }
                }} 
                onClick={() => navigate('/league/' + leagueId + '/team/' + myTeam?.id + '/poach/' + row.id)}
              >
                Poach
              </Button>
            </div>
          </Box>
        }
        return <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%', px: 1 }}>
          <BidOnPlayerButton 
            leagueId={leagueId!} 
            player={row.original} 
            teamId={myTeam?.id!} 
            fullWidth={true} 
            size={(isMobile ? "small" : "large") as 'small' | 'large'} 
          />
        </Box>
      }}
      
      //clicking anywhere on the row is now handled by the CompactPlayerCard on mobile
      muiTableBodyRowProps={({ row }) => ({
        sx: {
          // Apply position colors to all rows
          ...getColorByPosition(row.original.position || ''),
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.02)'
          }
        }
      })}

      muiTableBodyCellProps={({ column }) => ({
          //simple styling with the `sx` prop, works just like a style prop in this example
          sx: {
            color: 'black',
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
            verticalAlign: 'middle',
            // Special styling for the actions column
            ...(column.id === 'mrt-row-actions' && {
              height: { xs: '80px', sm: '90px' },
              padding: { xs: '8px 4px', sm: '16px 8px' }
            })
          },
      })}
      muiTableHeadCellProps={{
        sx: {
          "& .Mui-TableHeadCell-Content-Labels": {
            padding: { xs: '2px', sm: '4px' }
          },
          "& .MuiBox-root": {
            padding: { xs: '2px', sm: '4px' }
          },
          fontSize: { xs: '0.7rem', sm: '0.875rem' }
        },
      }}

      />
    </Box>
};

export default PlayerSearch;
